import React, { useEffect, useState } from "react";
import {
  DollarSign,
  TrendingDown,
  Target,
  ArrowUpRight,
  ArrowDownRight,
  Tag,
} from "lucide-react";
import { useHistory } from "react-router";

const iconMap = {
  totalCredit: DollarSign,
  totalDebit: TrendingDown,
  netBalance: Target,
  highCredit: ArrowUpRight,
  lowCredit: ArrowDownRight,
  highDebit: ArrowUpRight,
  lowDebit: ArrowDownRight,
  topCategory: Tag,
};

const gradientMap = [
  "linear-gradient(135deg,#60a5fa,#1e40af)",
  "linear-gradient(135deg,#f87171,#b91c1c)",
  "linear-gradient(135deg,#4ade80,#166534)",
  "linear-gradient(135deg,#a78bfa,#6d28d9)",
  "linear-gradient(135deg,#fb923c,#c2410c)",
  "linear-gradient(135deg,#22c55e,#15803d)",
  "linear-gradient(135deg,#38bdf8,#0369a1)",
  "linear-gradient(135deg,#84cc16,#4d7c0f)",
];

// 🔹 Static card layout (always visible)
const BASE_CARDS = [
  { title: "Total Credit Amount", icon: "totalCredit" },
  { title: "Highest Credit Month", icon: "highCredit" },
  { title: "Lowest Credit Month", icon: "lowCredit" },
  { title: "Net Balance", icon: "netBalance" },
  { title: "Total Debit Amount", icon: "totalDebit" },
  { title: "Highest Debit Month", icon: "highDebit" },
  { title: "Lowest Debit Month", icon: "lowDebit" },
  { title: "Top Spending Category", icon: "topCategory" },
];

export default function UniqueStatCards({
  selectedGlobalYear,
  selectedGlobalMonth,
  currency,
}) {
  const history = useHistory();
  const storedUserData = localStorage.getItem("userData");
  const groupId = storedUserData ? JSON.parse(storedUserData).groupId : "";

  const [cards, setCards] = useState(BASE_CARDS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!storedUserData) history.push("/login");
  }, [history, storedUserData]);

  useEffect(() => {
    const loadData = async () => {
      if (!groupId || !selectedGlobalYear) return;

      setLoading(true);

      try {
        const stats = await fetch(
          `https://dashboards.workanthem.com/api/stats?groupId=${groupId}&year=${selectedGlobalYear}`
        ).then((r) => r.json());

        const insights = await fetch(
          `https://dashboards.workanthem.com/api/financial-insights?groupId=${groupId}&year=${selectedGlobalYear}&month=${selectedGlobalMonth}`
        ).then((r) => r.json());

        setCards([
          {
            title: "Total Credit Amount",
            value: stats.curr.totalCredit,
            icon: "totalCredit",
          },
          {
            title: "Highest Credit Month",
            value: insights.highestCreditMonth.month,
            sub: `${currency}${parseInt(insights.highestCreditMonth.amount)}`,
            icon: "highCredit",
          },
          {
            title: "Lowest Credit Month",
            value: insights.lowestCreditMonth.month,
            sub: `${currency}${parseInt(insights.lowestCreditMonth.amount)}`,
            icon: "lowCredit",
          },
          {
            title: "Net Balance",
            value: Number(stats.netBalance),
            icon: "netBalance",
          },
          {
            title: "Total Debit Amount",
            value: stats.curr.totalDebit,
            icon: "totalDebit",
          },
          {
            title: "Highest Debit Month",
            value: insights.highestDebitMonth.month,
            sub: `${currency}${parseInt(insights.highestDebitMonth.amount)}`,
            icon: "highDebit",
          },
          {
            title: "Lowest Debit Month",
            value: insights.lowestDebitMonth.month,
            sub: `${currency}${parseInt(insights.lowestDebitMonth.amount)}`,
            icon: "lowDebit",
          },
          {
            title: "Top Spending Category",
            value: insights.topSpendingCategory.category,
            sub: `${currency}${parseInt(insights.topSpendingCategory.total)}`,
            icon: "topCategory",
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [groupId, selectedGlobalYear, selectedGlobalMonth, currency]);

  return (
    <div
      className="card-grid"
    >
      {cards.map((c, i) => {
        const Icon = iconMap[c.icon];

        return (
          <div
            key={i}
            style={{
              paddingLeft: 20,
              paddingTop: 16,
              height: 95,
              borderRadius: 14,
              background: gradientMap[i],
              color: "#fff",
              boxShadow: "0 12px 25px rgba(0,0,0,.25)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                right: 10,
                top: 10,
                opacity: 0.18,
              }}
            >
              <Icon size={56} />
            </div>

            <div style={{ position: "relative", zIndex: 2 }}>
              <div
                style={{
                  fontSize: 12,
                  textTransform: "uppercase",
                  fontWeight: 600,
                  marginBottom: 6,
                }}
              >
                {c.title}
              </div>

              {/* 🔹 Value */}
              <div style={{ fontSize: 18, fontWeight: 600 }}>
                {loading
                  ? "—"
                  : typeof c.value === "number"
                  ? `${currency}${c.value.toLocaleString()}`
                  : c.value}
              </div>

              {/* 🔹 Sub */}
              {!loading && c.sub && (
                <div style={{ fontSize: 11, opacity: 0.85 }}>{c.sub}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}